import { Button, Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { useEffect, useState } from 'react';
import { IS54FormData } from '../../NewHire/IS54Form';
import { fillIS54Form } from '../../../../services/IS54PDFService';

type IS54FormModalProps = {
  isOpen: boolean;
  data: IS54FormData | null;
  uploadedPdf?: string;
  toggleModal: (e?: any) => void;
};

const IS54FormModal = ({ isOpen, data, uploadedPdf, toggleModal }: IS54FormModalProps) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const staticPdfFileUrl =
    uploadedPdf || 'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/is-54-form.pdf';

  useEffect(() => {
    if (isOpen && data) {
      loadAndFillPdf();
    } else if (!isOpen) {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl('');
      }
      setError(null);
    }
  }, [isOpen, data]);

  const loadAndFillPdf = async () => {
    if (!data) return;
    setIsLoading(true);
    setError(null);
    try {
      const pdfBytes = await fillIS54Form(staticPdfFileUrl, data);
      if (pdfBytes) {
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const docUrl = URL.createObjectURL(blob);
        setPdfUrl(docUrl);
      } else {
        setError('Failed to generate PDF bytes.');
      }
    } catch (e) {
      console.error('Error loading/filling PDF:', e);
      setError(e instanceof Error ? e.message : 'An unknown error occurred while generating the PDF.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    const employeeName = data?.printEmployeeName?.replace(/\s+/g, '_') || 'employee';
    const filename = `IS54_Pay_Notice_${employeeName}.pdf`;
    link.setAttribute('download', filename);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCloseModal = (e?: any) => {
    toggleModal(e);
  };

  return (
    <>
      <Modal className="modal-dialog-centered" size="xl" isOpen={isOpen} toggle={handleCloseModal}>
        <ModalHeader className="pb-1" toggle={handleCloseModal}>
          IS54 Form
        </ModalHeader>
        <ModalBody className="pt-0">
          <Row className="mt-2">
            <Col md="12">
              <div className="d-flex justify-content-end">
                <Button
                  color="primary"
                  onClick={handleDownloadPdf}
                  size="sm"
                  disabled={!pdfUrl || isLoading || !!error}
                >
                  Download (PDF)
                </Button>
              </div>
            </Col>
            <Col md="12" className="mt-4" style={{ minHeight: '500px', maxHeight: '70vh', overflowY: 'auto' }}>
              {isLoading && <p className="text-center">Generating PDF...</p>}
              {error && <p className="text-center text-danger">Error: {error}</p>}
              {!isLoading && !error && pdfUrl && (
                <iframe
                  src={pdfUrl}
                  style={{ minHeight: 600, width: '100%', border: 'none' }}
                  title="IS54 Form Preview"
                />
              )}
              {!isLoading && !error && !pdfUrl && isOpen && <p className="text-center">No PDF to display.</p>}
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
};

export default IS54FormModal;
